import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { BottomSheet, BottomSheetContentLayout, Drawer, Spotlight, Typography, useIntentBehavior } from 'seagull';
import { getNotificationPresentationData } from '~/components/InAppNotificationsDrawer/InAppNotificationsDrawer.utils';
import { NOTIFICATIONS_TRIGGER_SPOTLIGHT_ID } from '~/components/NavigationAndStructure/InAppNotificationsButton/InAppNotificationsButton';
import { useEventReporter } from '~/event-reporter';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { timeModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import styles from './InAppNotificationsDrawer.module.scss';
import EmptyStateIllustration from './InAppNotificationsEmptyStateIllustration.svg';
const InAppNotificationsSpotlightBackdrop = (props)=>/*#__PURE__*/ _jsx(Spotlight, {
        spotlightId: NOTIFICATIONS_TRIGGER_SPOTLIGHT_ID,
        open: props.open,
        onClick: props.onClick
    });
export default function InAppNotificationsDrawer() {
    const isMobileDevice = useIsMobileForHomePage();
    const { data } = useStore((state)=>state.inAppNotifications);
    const worksAt = useStore(selectors.user.business);
    const email = useStore(selectors.user.email);
    const inAppNotificationsDrawerOpen = useStore((state)=>state.inAppNotificationsDrawerOpen);
    const { api, logBI, fetchInAppNotifications, setInAppNotificationsDrawerOpen } = useStoreActions();
    const { reportToHeap } = useEventReporter();
    const { getDisabled: isIntentDisabled } = useIntentBehavior();
    const { t: texts } = useLocaleKeys('components.inAppNotificationsDrawer');
    const headerText = {
        title: texts.title(),
        subtitle: worksAt === null || worksAt === void 0 ? void 0 : worksAt.name
    };
    useEffect(()=>{
        if (inAppNotificationsDrawerOpen && data && !isIntentDisabled(Intent.ManageNotifications)) {
            const unseenNotificationIds = data.filter((pn)=>pn.id && !pn.seen).map((pn)=>pn.id);
            if ((unseenNotificationIds === null || unseenNotificationIds === void 0 ? void 0 : unseenNotificationIds.length) > 0) {
                api.inAppNotifications.markAsSeen(unseenNotificationIds);
            }
        }
    }, [
        inAppNotificationsDrawerOpen,
        data,
        isIntentDisabled
    ]);
    const content = (data === null || data === void 0 ? void 0 : data.length) ? data.map((notificationItem, index)=>{
        var _notificationData_path;
        const notificationData = getNotificationPresentationData(notificationItem, texts.notifications);
        if (!notificationData) return null;
        const Element = notificationData.path ? NavLink : 'div';
        return /*#__PURE__*/ _jsxs(Element, {
            to: (_notificationData_path = notificationData.path) !== null && _notificationData_path !== void 0 ? _notificationData_path : '',
            id: notificationItem.id,
            "data-testid": "notification-item-".concat(index),
            "data-heapid": "notification-item",
            onClick: async ()=>{
                const notificationType = notificationItem[notificationItem.type].type;
                logBI(15001, {
                    notificationType
                });
                reportToHeap('notification-center', 'click', 'vendor-clicked-notification-item', {
                    email,
                    notificationType
                });
                if (!notificationItem.read && !isIntentDisabled(Intent.ManageNotifications)) {
                    api.inAppNotifications.markAsRead(notificationItem.id);
                    await fetchInAppNotifications();
                }
            },
            className: styles.item,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.icon,
                    children: notificationData.icon
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.main,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            "data-testid": "notification-item-description-".concat(notificationData.type, "-").concat(index),
                            "data-heapid": "notification-item-description",
                            children: /*#__PURE__*/ _jsx(Typography, {
                                children: notificationData.content
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            "data-testid": "notification-item-time-ago-".concat(index),
                            "data-heapid": "notification-item-time-ago",
                            children: /*#__PURE__*/ _jsx(Typography, {
                                skin: "secondary",
                                variant: "paragraph3",
                                children: timeModel.formatTimeAgo(notificationItem.timestamp)
                            })
                        })
                    ]
                }),
                !notificationItem.read && /*#__PURE__*/ _jsx("div", {
                    "data-testid": "read-item-dot",
                    className: styles.readItemDotContainer,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.readItemDot
                    })
                })
            ]
        }, notificationItem.id);
    }) : /*#__PURE__*/ _jsxs("div", {
        className: styles.emptyState,
        "data-testid": "notification-center-drawer-empty-state",
        children: [
            /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                children: texts.emptyTitle()
            }),
            /*#__PURE__*/ _jsx(Typography, {
                children: texts.emptyMessage()
            })
        ]
    });
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": "notification-center",
        children: isMobileDevice ? /*#__PURE__*/ _jsx(BottomSheet, {
            open: inAppNotificationsDrawerOpen,
            onClose: ()=>setInAppNotificationsDrawerOpen(false),
            paddingBottom: 0,
            BackdropComponent: InAppNotificationsSpotlightBackdrop,
            children: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                header: {
                    title: headerText.title,
                    subtitle: headerText.subtitle,
                    paddingBottom: 8
                },
                children: content
            })
        }) : /*#__PURE__*/ _jsx(Drawer, {
            hasTopbar: true,
            header: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "h5",
                        weight: "bold",
                        children: headerText.title
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        skin: "secondary",
                        variant: "paragraph3",
                        children: headerText.subtitle
                    })
                ]
            }),
            open: inAppNotificationsDrawerOpen,
            onClose: ()=>setInAppNotificationsDrawerOpen(false),
            onCloseButtonClick: ()=>setInAppNotificationsDrawerOpen(false),
            testId: "notification-center-drawer",
            BackdropComponent: InAppNotificationsSpotlightBackdrop,
            children: content
        })
    });
}
